import pluralize from 'pluralize'
import {SessionFeedbackButtons} from './feedback/SessionFeedbackButtons'
import {getReasoningEffortLabel, type ReasoningEffort} from '@github-ui/copilot-shared-ui/utils/reasoning-effort'
import styles from './SessionFooter.module.css'
import {CopyToClipboardButton} from '@github-ui/copy-to-clipboard/Button'
import {useSessionContext} from '../../contexts/SessionContext'
import {useSelectedModel} from '../../hooks/use-selected-model'
import {getSessionFooterUsage, type SessionFooterUsage} from '../../utils/session-usage'
import {useIsAhpSession} from '../../hooks/use-is-ahp-session'
import {isActiveSession, SessionState} from '../../types/session'
import {useAgentProfile} from '../../contexts/agent-profile-context'
import {clsx} from 'clsx'

interface SessionFooterProps {
  showFeedbackButtons: boolean
  usage?: SessionFooterUsage
  copyText?: string
  model?: string
  reasoningEffort?: ReasoningEffort
  variant?: 'legacy' | 'metadata-bar' | 'embedded'
}

export function SessionFooter({
  showFeedbackButtons,
  usage,
  copyText,
  model,
  reasoningEffort,
  variant = 'legacy',
}: SessionFooterProps) {
  const usageLabel =
    usage?.type === 'ai-credits'
      ? `${usage.amount} AI credits`
      : usage?.type === 'premium-requests'
        ? `${usage.amount} premium ${pluralize('request', usage.amount)}`
        : undefined
  const reasoningLabel =
    reasoningEffort &&
    (reasoningEffort === 'none' ? 'No reasoning' : `${getReasoningEffortLabel(reasoningEffort)} reasoning`)
  const modelLabel = model && reasoningLabel ? `${model} (${reasoningLabel})` : (model ?? reasoningLabel)
  const metadataItems =
    variant === 'metadata-bar'
      ? [
          {key: 'model', value: modelLabel},
          {key: 'usage', value: usageLabel},
        ]
      : [
          {key: 'usage', value: usageLabel},
          {key: 'model', value: model},
          {key: 'reasoning', value: reasoningLabel},
        ]
  const visibleMetadataItems = metadataItems.filter((item): item is {key: string; value: string} => Boolean(item.value))

  return (
    <div
      className={clsx(
        styles.footer,
        variant === 'metadata-bar' && styles.metadataBar,
        variant === 'embedded' && styles.embedded,
      )}
    >
      <div className={styles.actions}>
        {showFeedbackButtons && <SessionFeedbackButtons />}
        {copyText && (
          <CopyToClipboardButton size="small" textToCopy={copyText} variant="invisible" aria-label="Copy response" />
        )}
      </div>
      {visibleMetadataItems.length > 0 && (
        <div className={clsx(styles.metadata, variant === 'metadata-bar' && styles.inlineMetadata)}>
          {visibleMetadataItems.map((item, index) => (
            <span key={item.key}>
              {index > 0 && <span className={styles.separator}> · </span>}
              {item.value}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

interface SessionMetadataBarProps {
  placement: 'inside-session' | 'outside-session'
  copyText?: string
  loading?: boolean
}

export function SessionMetadataBar({placement, copyText, loading = false}: SessionMetadataBarProps) {
  const {session, reconciledAiCredits} = useSessionContext()
  const selectedModel = useSelectedModel(session.model)
  const {isAhpSession, loading: ahpEligibilityPending} = useIsAhpSession(session)
  const {interactiveSession} = useAgentProfile()
  const isComplete = session.state === SessionState.Completed
  const isPullRequestComment = session.event_type === 'pull_request_comment'
  const rendersOutsideSession = isComplete && (!interactiveSession || isPullRequestComment)
  const belongsAtPlacement = placement === 'outside-session' ? rendersOutsideSession : !rendersOutsideSession

  if (
    loading ||
    ahpEligibilityPending ||
    isAhpSession ||
    !belongsAtPlacement ||
    (session.state !== SessionState.Completed && session.state !== SessionState.InProgress)
  ) {
    return null
  }

  const usage = getSessionFooterUsage(session, reconciledAiCredits)

  return (
    <SessionFooter
      showFeedbackButtons={!isActiveSession(session)}
      usage={usage}
      copyText={copyText}
      model={selectedModel?.displayName}
      reasoningEffort={session.reasoning_effort}
      variant="metadata-bar"
    />
  )
}
